import React, { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";

// Kendi JSON dosyamızdan verileri içe aktarıyoruz.
import categories from "./json/categories.json";

// Ikonlar (örnek olarak lucide-react içerisinden)
import { Laptop, Tablet, Watch, Gamepad2 } from "lucide-react";

// İLGİLİ KATEGORİLER DİZİSİ
// Bu liste, kategori adı (`name`) ve JSON'daki categories nesnesinin anahtarları ile *birebir* uyuşuyor.
const categoriesList = [
  {
    name: "Tablet Sat",
    alt: "Tablet kategorisi",
    icon: <Tablet size={48} strokeWidth={1.5} />,
  },
  {
    name: "Akıllı Saat Sat",
    alt: "Akıllı saat kategorisi",
    icon: <Watch size={48} strokeWidth={1.5} />,
  },
  {
    name: "Bilgisayar Sat",
    alt: "Bilgisayar kategorisi",
    icon: <Laptop size={48} strokeWidth={1.5} />,
  },
  {
    name: "Oyun Konsolu Sat",
    alt: "Oyun konsolu kategorisi",
    icon: <Gamepad2 size={48} strokeWidth={1.5} />,
  },
];


const CategoryDetails = ({ selectedCategory, products }) => {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <motion.div
      ref={ref}
      className="container mx-auto p-4 max-w-7xl bg-gray-200 rounded-lg mt-8 shadow-lg mb-8"
      initial={{ opacity: 0, y: 50 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5 }}
    >
      {selectedCategory && (
        <>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            {selectedCategory} için Markalar
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {products.map((product, index) => (
              <motion.div
                key={product.name + index}
                className="col-span-1 mb-4 relative group overflow-hidden"
                initial={{ opacity: 0, y: 50 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.2 }}
              >
                <div className="bg-gray-50 shadow-md rounded-lg h-full relative group">
                  {/* Kartın İçeriği */}
                  <div className="flex items-center p-6 relative group-hover:blur-md transition duration-500">
                    {/* Sol Kısım: Logo ve İsim */}
                    <div className="flex flex-col items-center w-1/2">
                      <img
                        src={product.logo || "https://via.placeholder.com/150"}
                        alt={product.alt || product.name}
                        className="mb-4 w-16"
                      />
                      <p className="text-center text-lg font-medium text-gray-700">
                        {product.name}
                      </p>
                    </div>
                    {/* Sağ Kısım: Ürün Görseli */}
                    <div className="flex justify-center items-center w-1/2">
                      <img
                        src={product.productImage || "https://via.placeholder.com/300"}
                        alt={product.alt || product.name}
                        className="w-60 object-contain"
                      />
                    </div>
                  </div>

                  {/* Hover ile Görünecek Katman */}
                  <div className="absolute inset-0 bg-white/10 bg-opacity-70 backdrop-blur-md flex flex-col justify-center items-center opacity-0 group-hover:opacity-100 transition-all duration-500 pointer-events-none">
                    <p className="text-lg font-semibold text-gray-800">
                      {product.name}
                    </p>
                    <p className="text-lg text-gray-800 mt-2">
                      {product.name} model ürün
                    </p>
                  </div>

                  {/* Alt Kısım: İncele / Sat Butonu */}
                  <button
                    className="w-full bg-black text-white text-center py-3 rounded-b-lg z-10 relative transition duration-700 hover:bg-gradient-to-r hover:from-orange-500 hover:via-red-500 hover:to-pink-500"
                    onClick={() => window.open(product.link, "_blank")}
                  >
                    <span className="text-sm uppercase tracking-wide">
                      Hemen Sat
                    </span>
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </>
      )}
    </motion.div>
  );
};


const ProductCategoryV2 = () => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const detailsRef = useRef(null);

  const handleCategoryClick = (categoryName) => {
    setSelectedCategory(categoryName);

    // Kategoriye tıklandığında detay bölümüne scroll
    const offset = 150; // Yukarıda boşluk bırakmak için
    const elementPosition = detailsRef.current.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.scrollY - offset;

    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    if (selectedCategory) {
      // categories.json içinden, seçili kategorinin ürünlerini al
      const products = categories[selectedCategory];
      setFilteredProducts(products || []);
    }
  }, [selectedCategory]);

  return (
    <>
      {/* Kategoriler Listesi */}
      <div className="container mx-auto p-4 max-w-7xl">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {categoriesList.map((category) => (
            <div
              key={category.name}
              className="relative bg-white shadow-lg rounded-lg overflow-hidden group cursor-pointer transform transition duration-300 hover:scale-105"
              onClick={() => handleCategoryClick(category.name)}
            >
              {/* Üst Kısım: Gradient ve İkon */}
              <div className="h-36 flex items-center justify-center bg-gradient-to-r from-black via-blue-950 to-black group-hover:from-blue-950 group-hover:via-black group-hover:to-blue-950 transition duration-300 rounded-t-lg">
                <div className="text-white text-5xl group-hover:scale-110 transition-transform bg-white/5 p-4 rounded-full shadow-md">
                  {category.icon}
                </div>
              </div>

              <br />

              {/* Alt Kısım: Kategori Bilgisi */}
              <div className="p-4 text-center">
                <h3 className="text-xl font-bold text-gray-800">
                  {category.name}
                </h3>
                <p className="text-sm text-gray-600 mt-2">
                  {category.alt || "Kategori açıklaması burada yer alır."}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Seçilen Kategorinin Detayları */}
      <div ref={detailsRef}>
        {selectedCategory && (
          <CategoryDetails
            selectedCategory={selectedCategory}
            products={filteredProducts}
          />
        )}
      </div>
    </>
  );
};

export default ProductCategoryV2;
